import React from "react";
import { View, Text, TouchableOpacity, StyleSheet } from "react-native";
import type { NormalizedRelease, NormalizedReleaseItem} from "@rista/core/whatsNewSection";
import type { ReleaseCategory } from "@rista/core/types/releaseData"; 

export function getBadgeColors(category: ReleaseCategory) {
  switch (category) {
    case "New":
      return { background: "#eff6ff", color: "#2563eb" };
    case "Improved":
      return { background: "#ecfdf5", color: "#059669" };
    case "Fixed":
      return { background: "#fffbeb", color: "#d97706" };
    default:
      return { background: "#f3f4f6", color: "#374151" };
  }
}

export function renderBulletItems(items: NormalizedReleaseItem[] = [], keyPrefix: string, stylesOverride?: any) {
  const s = stylesOverride || styles;
  return items.map((item, index) => (
    <View key={`${keyPrefix}-${index}`} style={s.bulletRow}>
      <Text style={s.bulletDot}>▪</Text>
      <Text style={s.bulletTextWrap}>
        <Text style={s.bulletTitle}>{item.title}</Text>
        <Text style={s.bulletDesc}> — {item.description}</Text>
      </Text>
    </View>
  ));
}

export function renderBulletGroups(release?: NormalizedRelease, stylesOverride?: any) {
  if (!release) return null;
  const s = stylesOverride || styles;

  return (
    <View style={s.groupedWrap}>
      {release.newItems?.length ? (
        <View style={s.groupBlock}>
          <Text style={[s.groupTitle, s.newTitle]}>NEW</Text>
          {renderBulletItems(release.newItems, "new", s)}
        </View>
      ) : null}

      {release.improvedItems?.length ? (
        <View style={s.groupBlock}>
          <Text style={[s.groupTitle, s.improvedTitle]}>IMPROVED</Text>
          {renderBulletItems(release.improvedItems, "improved", s)}
        </View>
      ) : null}

      {release.fixedItems?.length ? (
        <View style={s.groupBlock}>
          <Text style={[s.groupTitle, s.fixedTitle]}>FIXED</Text>
          {renderBulletItems(release.fixedItems, "fixed", s)}
        </View>
      ) : null}
    </View>
  );
}

export function renderBadgeRows(items: NormalizedReleaseItem[] = [], keyPrefix: string, stylesOverride?: any, badgeColorsFn?: any) {
  const s = stylesOverride || styles;
  const getColors = badgeColorsFn || getBadgeColors;
  return items.map((item, index) => {
    const badge = getColors(item.category as ReleaseCategory);

    return (
      <View key={`${keyPrefix}-${index}`} style={s.badgeRow}>
        <View style={s.badgeRowContent}>
          <Text style={s.itemTitle}>{item.title}</Text>
          <Text style={s.itemDescription}>{item.description}</Text>
        </View>
        <View style={[s.badge, { backgroundColor: badge.background }]}>
          <Text style={[s.badgeText, { color: badge.color }]}>{item.category}</Text>
        </View>
      </View>
    );
  });
}

export function renderAllBadgeItems(release?: NormalizedRelease, stylesOverride?: any, badgeColorsFn?: any) {
  if (!release) return null;
  const allItems = [
    ...(release.newItems || []),
    ...(release.improvedItems || []),
    ...(release.fixedItems || []),
  ];

  return <View>{renderBadgeRows(allItems, "all", stylesOverride, badgeColorsFn)}</View>;
}

const styles = StyleSheet.create({
  // minimal styles to match existing shape; actual file's styles are in parent index.tsx
  groupedWrap: { paddingHorizontal: 20 },
  groupBlock: { marginBottom: 12 },
  groupTitle: { fontSize: 12, fontWeight: '700', marginBottom: 8 },
  newTitle: { color: '#2563eb' },
  improvedTitle: { color: '#059669' },
  fixedTitle: { color: '#d97706' },
  bulletRow: { flexDirection: 'row', marginBottom: 8 },
  bulletDot: { marginRight: 8 },
  bulletTextWrap: { flex: 1 },
  bulletTitle: { fontWeight: '600' },
  bulletDesc: { color: '#4b5563' },
  badgeRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: 12, paddingHorizontal: 20, borderBottomWidth: 1, borderBottomColor: '#f3f4f6' },
  badgeRowContent: { flex: 1, paddingRight: 12 },
  itemTitle: { fontWeight: '600' },
  itemDescription: { color: '#6b7280' },
  badge: { paddingHorizontal: 8, paddingVertical: 6, borderRadius: 6 },
  badgeText: { fontWeight: '700', fontSize: 12 },
});

export default {
  getBadgeColors,
  renderBulletItems,
  renderBulletGroups,
  renderBadgeRows,
  renderAllBadgeItems,
  
};
